import type { Edge, Node } from '@xyflow/react';
import type { NodeTemplate } from '@features/flows/palette/registry';
import { resolveEdgeSchemas } from './edge-context';

const isDevEnv = (() => {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
      return Boolean((import.meta as any).env?.DEV);
    }
    if (typeof process !== 'undefined') {
      return process.env.NODE_ENV !== 'production';
    }
  } catch {
    /* ignore */
  }
  return true;
})();

const debugSummary = (...args: unknown[]) => {
  if (!isDevEnv) return;
  // eslint-disable-next-line no-console
  console.debug('[autocast][summarizeEdgeMapping]', ...args);
};

export type EdgeMappingSummary = { from: string; to: string; multi: number };

const stripArraySuffix = (segment: string) => segment.replace(/\[\]/g, '');

const splitPath = (path: string | null | undefined): string[] => {
  if (!path) return [];
  return String(path)
    .split('.')
    .map((segment) => stripArraySuffix(segment))
    .filter(Boolean);
};

const startsWithPath = (candidate: string, root: string | null | undefined): boolean => {
  if (!root) return true;
  const candidateSegs = splitPath(candidate);
  const rootSegs = splitPath(root);
  if (!rootSegs.length) return true;
  if (rootSegs.length > candidateSegs.length) return false;
  return rootSegs.every((seg, idx) => candidateSegs[idx] === seg);
};

const summarizeFromJSONata = (
  jsonata: string,
  target: string | null | undefined,
  fallbackTarget: string | null | undefined,
): EdgeMappingSummary | null => {
  const trimmed = jsonata.trim();
  if (!trimmed) return null;

  const cleanJSONataPath = (raw: string) =>
    raw
      .replace(/^\$\./, '')
      .replace(/\["/g, '.')
      .replace(/"\]/g, '')
      .replace(/\[(\d+|\*)\]/g, '[]')
      .trim();

  try {
    const primaryPattern = /\$\.(?:\["([^"]+)"\]|[A-Za-z0-9_]+)(?=[\.[\)\s,:}])/g;
    const primaryMatches = Array.from(trimmed.matchAll(primaryPattern))
      .map((match) => match?.[0] ?? '')
      .filter(Boolean);

    debugSummary('jsonata raw', trimmed);
    debugSummary('jsonata primary matches', primaryMatches);

    let rawMatches = primaryMatches;

    if (rawMatches.length === 0) {
      const fallbackPattern = /:\s*([A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*(?:\[[^\]]*\])?)/g;
      const fallbackMatches: string[] = [];
      for (const match of trimmed.matchAll(fallbackPattern)) {
        const candidate = (match[1] ?? '').trim();
        if (!candidate) continue;
        if (!candidate.includes('.') && !candidate.includes('[')) continue;
        fallbackMatches.push(candidate);
      }
      debugSummary('jsonata fallback matches', fallbackMatches);
      rawMatches = fallbackMatches;
    }

    if (rawMatches.length > 0) {
      const cleanedPaths = rawMatches.map(cleanJSONataPath).filter(Boolean);
      debugSummary('jsonata cleanedPaths', cleanedPaths);
      if (cleanedPaths.length > 0) {
        const from = cleanedPaths[0]!;
        const to = target ?? fallbackTarget ?? '';
        const uniqueInputs = new Set(cleanedPaths.map((path) => path.split('.')[0] ?? path));
        debugSummary('jsonata summary candidate', { from, to, multi: uniqueInputs.size || 1 });
        return { from, to, multi: uniqueInputs.size || 1 };
      }
    }
  } catch {
    debugSummary('jsonata parsing failed');
  }

  const isLiteralCandidate =
    trimmed.startsWith('{') ||
    trimmed.startsWith('[') ||
    trimmed.startsWith('"') ||
    trimmed.startsWith("'") ||
    /^[0-9.+-]+$/.test(trimmed);
  if (isLiteralCandidate) {
    const to = target ?? fallbackTarget ?? '';
    debugSummary('jsonata literal summary', { to });
    return { from: 'literal', to, multi: 1 };
  }

  return null;
};

export function summarizeEdgeMapping(
  edge: Edge,
  nodes: Node[] | Record<string, Node>,
  templates: Map<string, NodeTemplate> | null | undefined,
): EdgeMappingSummary | null {
  const data = (edge.data ?? {}) as Record<string, unknown>;
  const mappingLiquid = data.mappingLiquid as Record<string, string> | undefined;
  const context = resolveEdgeSchemas(edge, nodes, templates);
  const targetRoot = context.targetInputPath ?? context.rawTarget ?? null;
  debugSummary('edge', edge.id, {
    hasLiquid: Boolean(mappingLiquid && Object.keys(mappingLiquid).length),
    hasJsonata: typeof data.mappingJSONata === 'string' && data.mappingJSONata.trim().length > 0,
    targetRoot,
  });

  if (mappingLiquid && Object.keys(mappingLiquid).length) {
    const extract = (tpl: string): { from?: string; isArray?: boolean } => {
      try {
        const inner = String(tpl).match(/\{\{\s*([^}]+?)\s*\}\}/)?.[1] ?? '';
        const head = inner.split('|')[0]?.trim() ?? '';
        if (!head) return {};
        const isArray = /\|\s*map/i.test(inner);
        return { from: head, isArray };
      } catch {
        return {};
      }
    };

    const entries = Object.entries(mappingLiquid);
    const candidates = entries
      .filter(([to]) => startsWithPath(to, targetRoot))
      .map(([to, code]) => ({ to, ...extract(code) }));
    const bucket = candidates.length
      ? candidates
      : entries.map(([to, code]) => ({ to, ...extract(code) }));
    const primary = bucket.find((c) => c.from) ?? bucket[0];
    debugSummary('liquid evaluation', {
      entries: entries.length,
      candidates: candidates.length,
      bucket: bucket.length,
      primary,
    });
    if (primary?.from) {
      const label = primary.isArray ? `${stripArraySuffix(primary.from)}[]` : stripArraySuffix(primary.from);
      debugSummary('liquid summary resolved', { from: label, to: primary.to, multi: bucket.length || 1 });
      return { from: label, to: primary.to, multi: bucket.length || 1 };
    }
    debugSummary('liquid summary skipped');
  }

  const jsonata = typeof data.mappingJSONata === 'string' ? data.mappingJSONata : null;
  if (jsonata) {
    const summary = summarizeFromJSONata(jsonata, context.targetInputPath ?? null, context.rawTarget ?? null);
    debugSummary('jsonata evaluation', {
      hasJsonata: Boolean(jsonata),
      summary,
    });
    if (summary) return summary;
  }

  debugSummary('summary unavailable');
  return null;
}
